import type { HistoricProcessInstance, HistoricTaskInstance } from '@/lib/camunda/history-types';
import {
  CamundaHistoryService,
  type ParticipationStartForm,
} from '@/lib/camunda/history-service';
import type { ProcessHistoryTaskItem } from './process-history-types';

export function resolveWorkableTaskId(
  historicTask: HistoricTaskInstance,
  availableRuntimeTaskIds: Set<string>,
): string | null {
  if (historicTask.endTime) {
    return null;
  }

  if (availableRuntimeTaskIds.has(historicTask.id)) {
    return historicTask.id;
  }

  return null;
}

export function findUnmatchedActiveTaskIds(
  historicTasks: Pick<HistoricTaskInstance, 'id'>[],
  activeTasks: Pick<HistoricTaskInstance, 'id'>[],
): string[] {
  const historicTaskIds = new Set(historicTasks.map((task) => task.id));
  const unmatchedTaskIds: string[] = [];
  for (const task of activeTasks) {
    if (!historicTaskIds.has(task.id)) {
      unmatchedTaskIds.push(task.id);
    }
  }
  return unmatchedTaskIds;
}

const START_FORM_TASK_ID_PREFIX = 'start:';

export function decodeHistoryTaskRouteId(taskId: string): string {
  return decodeURIComponent(taskId);
}

function buildStartFormTaskId(processInstanceId: string): string {
  return `${START_FORM_TASK_ID_PREFIX}${processInstanceId}`;
}
export function buildSyntheticStartFormTask(
  process: HistoricProcessInstance,
  startEvent: Pick<ParticipationStartForm, 'activityId' | 'name'>,
): HistoricTaskInstance {
  return {
    id: buildStartFormTaskId(process.id),
    processDefinitionKey: process.processDefinitionKey,
    processDefinitionId: process.processDefinitionId,
    processInstanceId: process.id,
    executionId: process.id,
    caseDefinitionKey: null,
    caseDefinitionId: null,
    caseInstanceId: null,
    caseExecutionId: null,
    activityInstanceId: `${startEvent.activityId}:${process.id}`,
    name: startEvent.name ?? 'Formulario de inicio',
    description: null,
    deleteReason: null,
    owner: null,
    assignee: process.startUserId,
    startTime: process.startTime,
    endTime: process.startTime,
    duration: 0,
    taskDefinitionKey: startEvent.activityId,
    priority: 50,
    due: null,
    parentTaskId: null,
    followUp: null,
    tenantId: process.tenantId,
    removalTime: null,
    rootProcessInstanceId: process.rootProcessInstanceId ?? process.id,
  };
}

export function applyHistoricTaskDetailAccess(
  tasks: Omit<ProcessHistoryTaskItem, 'canViewDetail' | 'displayName' | 'taskOrdinal'>[],
  viewableTaskIds: Set<string>,
): Omit<ProcessHistoryTaskItem, 'displayName' | 'taskOrdinal'>[] {
  return tasks.map((item) => ({
    ...item,
    canViewDetail: CamundaHistoryService.canUserViewHistoricTaskDetail(item.task.id, {
      viewableTaskIds,
    }),
  }));
}

export function hasStartFormInTaskList(
  tasks: Pick<ProcessHistoryTaskItem, 'isStartForm' | 'task'>[],
  startActivityId: string,
): boolean {
  return tasks.some(
    (item) =>
      item.isStartForm || item.task.taskDefinitionKey === startActivityId,
  );
}

type TaskItemWithDisplay = Omit<ProcessHistoryTaskItem, 'displayName' | 'taskOrdinal' | 'canViewDetail'>;

export function applyTaskDisplayNames<T extends TaskItemWithDisplay>(tasks: T[]): Array<T & {
  displayName: string;
  taskOrdinal: number;
}> {
  const ordinalsByDefinitionKey = new Map<string, number>();

  return tasks.map((item) => {
    const definitionKey = item.task.taskDefinitionKey ?? item.task.id;
    const ordinal = (ordinalsByDefinitionKey.get(definitionKey) ?? 0) + 1;
    ordinalsByDefinitionKey.set(definitionKey, ordinal);

    const baseName = item.task.name || item.task.taskDefinitionKey || 'Formulario histórico';
    const displayName = ordinal > 1 ? `${baseName} (${ordinal})` : baseName;

    return {
      ...item,
      displayName,
      taskOrdinal: ordinal,
    };
  });
}
